import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "@/components/Nav";
import { ProofDocsButton } from "@/components/ProofDocs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles, Zap, ArrowUpRight, Layers } from "lucide-react";
import { DiscrepancyFinder } from "@/components/DiscrepancyFinder";
import { useAuth } from "@/context/AuthContext";

const TYPES = [
  { value: "collection", label: "Collection" },
  { value: "late_payment", label: "Late Payment" },
  { value: "charge_off", label: "Charge-off" },
  { value: "inquiry", label: "Hard Inquiry" },
  { value: "bankruptcy", label: "Bankruptcy" },
  { value: "repossession", label: "Repossession" },
  { value: "other", label: "Other" },
];

const BUREAUS = [
  { value: "all", label: "All bureaus" },
  { value: "equifax", label: "Equifax" },
  { value: "experian", label: "Experian" },
  { value: "transunion", label: "TransUnion" },
];

const STRATEGIES = [
  { value: "inaccurate", label: "Inaccurate information" },
  { value: "not_mine", label: "Not my account" },
  { value: "outdated", label: "Outdated (7+ years)" },
  { value: "identity_theft", label: "Identity theft" },
  { value: "paid_not_updated", label: "Paid but not updated" },
  { value: "goodwill", label: "Goodwill removal" },
];

export default function NegativeItems() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({
    creditor: "",
    account_number: "",
    item_type: "collection",
    amount: "",
    date_reported: "",
    bureau: "all",
    reason: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const [genOpen, setGenOpen] = useState(false);
  const [genItem, setGenItem] = useState(null);
  const [genBureau, setGenBureau] = useState("all");
  const [genStrategy, setGenStrategy] = useState("inaccurate");
  const [genLoading, setGenLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(null); // bureau name currently drafting

  // Group counts per bureau for the bulk-draft strip
  const byBureau = items.reduce((acc, it) => {
    const b = it.bureau === "all" ? null : it.bureau;
    if (b) acc[b] = (acc[b] || 0) + 1;
    return acc;
  }, {});

  const runBulk = async (bureau) => {
    // Paywall FIRST — free users go straight to /pricing instead of the confirm+400 dance.
    if (!user || user.plan === "free") {
      toast.info("Bulk drafting is a Basic or Plus feature — memberships from $29.99/mo.");
      navigate("/app/pricing?from=bulk-draft");
      return;
    }
    const count = byBureau[bureau] || 0;
    if (count === 0) return;
    if (!window.confirm(
      `Draft dispute letters for all ${count} ${bureau.charAt(0).toUpperCase() + bureau.slice(1)} item${count === 1 ? "" : "s"}? This will use ${count} AI generations.`
    )) return;
    setBulkLoading(bureau);
    try {
      const { data } = await api.post("/disputes/bulk-generate", { bureau });
      const msg = data.created_count > 0
        ? `Drafted ${data.created_count} letter${data.created_count === 1 ? "" : "s"} for ${bureau.charAt(0).toUpperCase() + bureau.slice(1)}`
        : data.message || "Nothing new to draft";
      if (data.failed_count > 0) {
        toast.warning(`${msg} (${data.failed_count} failed)`);
      } else {
        toast.success(msg);
      }
      if (data.created_count > 0) {
        navigate("/app/disputes");
      }
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.info("Bulk drafting is a Basic or Plus feature — memberships from $29.99/mo.");
        navigate("/app/pricing");
        return;
      }
      toast.error(formatApiError(err));
    } finally {
      setBulkLoading(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/negative-items");
      setItems(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submitAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/negative-items", {
        ...form,
        amount: form.amount ? parseFloat(form.amount) : null,
      });
      toast.success("Item added");
      setAddOpen(false);
      setForm({
        creditor: "",
        account_number: "",
        item_type: "collection",
        amount: "",
        date_reported: "",
        bureau: "all",
        reason: "",
        notes: "",
      });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this item and its disputes?")) return;
    try {
      await api.delete(`/negative-items/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openGenerate = (item) => {
    // Paywall FIRST — don't even open the dialog if the user isn't paid.
    // A free user clicking "letter" should feel the paywall immediately, not after picking options.
    if (!user || user.plan === "free") {
      toast.info("Drafting letters is a Basic or Plus feature — memberships from $29.99/mo.");
      navigate("/app/pricing?from=draft");
      return;
    }
    setGenItem(item);
    setGenStrategy(item.recommended_strategy || "inaccurate");
    setGenBureau("all");
    setGenOpen(true);
  };

  const submitGenerate = async () => {
    if (!genItem) return;
    setGenLoading(true);
    try {
      if (genBureau === "all") {
        // Draft one letter per bureau — the recommended play. Fire in parallel.
        const bureaus = ["equifax", "experian", "transunion"];
        const results = await Promise.allSettled(
          bureaus.map((b) =>
            api.post("/disputes/generate", {
              negative_item_id: genItem.id,
              bureau: b,
              strategy: genStrategy,
            })
          )
        );
        const succeeded = results.filter((r) => r.status === "fulfilled");
        const failed = results.filter((r) => r.status === "rejected");
        // If any 403 slipped through, route to pricing
        const anyPaywall = failed.some((r) => r.reason?.response?.status === 403);
        if (anyPaywall) {
          toast.info("Drafting letters is a Basic or Plus feature — memberships from $29.99/mo.");
          setGenOpen(false);
          navigate("/app/pricing");
          return;
        }
        if (succeeded.length === 0) {
          toast.error("Draft failed. Try again.");
        } else {
          toast.success(
            `Drafted ${succeeded.length}/3 letters — one for each bureau${failed.length ? " (some failed, retry)" : ""}`
          );
          setGenOpen(false);
          navigate("/app/disputes");
        }
        return;
      }
      const { data } = await api.post("/disputes/generate", {
        negative_item_id: genItem.id,
        bureau: genBureau,
        strategy: genStrategy,
      });
      toast.success("Letter drafted");
      setGenOpen(false);
      navigate(`/app/disputes/${data.id}`);
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.info("Drafting letters is a Basic or Plus feature — memberships from $29.99/mo.");
        setGenOpen(false);
        navigate("/app/pricing");
        return;
      }
      toast.error(formatApiError(err));
    } finally {
      setGenLoading(false);
    }
  };

  return (
    <AppShell>
      <div className="flex items-start justify-between mb-10 gap-4 flex-wrap">
        <div>
          <div className="overline mb-3">Negative items</div>
          <h1 className="font-display text-5xl md:text-6xl leading-none">Everything to dispute.</h1>
          <p className="text-[#A8ADA5] mt-3 max-w-2xl">
            Items from your AI scans + anything you added. One click drafts an FCRA letter.
          </p>
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-item-btn" className="rounded-full btn-lime h-11">
              <Plus size={16} className="mr-2" /> Add item
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg bg-[#131518] border-[#26282C] text-[#F5F3EE]">
            <DialogHeader>
              <DialogTitle className="font-display text-3xl">Add a negative item</DialogTitle>
            </DialogHeader>
            <form onSubmit={submitAdd} className="space-y-3" data-testid="add-item-form">
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#6D716C] font-mono">Creditor / Furnisher *</Label>
                <Input required value={form.creditor} onChange={(e) => setForm({ ...form, creditor: e.target.value })} data-testid="item-creditor-input" className="mt-1 rounded-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#6D716C] font-mono">Type</Label>
                  <Select value={form.item_type} onValueChange={(v) => setForm({ ...form, item_type: v })}>
                    <SelectTrigger data-testid="item-type-select" className="mt-1 rounded-sm bg-[#0B0C0E] border-[#26282C]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#131518] border-[#26282C] text-[#F5F3EE]">
                      {TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#6D716C] font-mono">Bureau</Label>
                  <Select value={form.bureau} onValueChange={(v) => setForm({ ...form, bureau: v })}>
                    <SelectTrigger className="mt-1 rounded-sm bg-[#0B0C0E] border-[#26282C]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#131518] border-[#26282C] text-[#F5F3EE]">
                      {BUREAUS.map((b) => (
                        <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#6D716C] font-mono">Account #</Label>
                  <Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} className="mt-1 rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#6D716C] font-mono">Amount ($)</Label>
                  <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1 rounded-sm" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#6D716C] font-mono">Date reported</Label>
                <Input placeholder="e.g. 2023-06 or June 2023" value={form.date_reported} onChange={(e) => setForm({ ...form, date_reported: e.target.value })} className="mt-1 rounded-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#6D716C] font-mono">Reason to dispute</Label>
                <Textarea rows={3} placeholder="e.g. I never had an account with this company." value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1 rounded-sm" />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={saving} data-testid="item-save-btn" className="rounded-full btn-lime">
                  {saving ? "Saving…" : "Save item"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-[#A8ADA5]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="card-surface p-16 text-center">
          <div className="font-display text-3xl mb-2">Nothing here yet.</div>
          <p className="text-[#A8ADA5] mb-8">Run an AI scan or add an item manually to get started.</p>
          <div className="flex gap-3 justify-center">
            <Button className="rounded-full btn-lime" onClick={() => navigate("/app/scan")} data-testid="items-empty-scan-btn">
              Run AI Scan
            </Button>
            <Button className="rounded-full btn-outline" onClick={() => setAddOpen(true)}>
              Add manually
            </Button>
          </div>
        </div>
      ) : (
        <>
          {/* Cross-bureau discrepancy finder (guaranteed-win disputes) */}
          <DiscrepancyFinder />

          {/* Bulk draft strip */}
          {Object.keys(byBureau).length > 0 && (
            <div className="card-surface p-5 mb-4" data-testid="bulk-draft-strip">
              <div className="flex items-start gap-3 mb-3">
                <Zap size={16} className="text-[#D5FF3F] flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-medium text-[#F5F3EE]">Draft every letter for one bureau</div>
                  <div className="text-xs text-[#A8ADA5]">
                    One click drafts a bureau-grade dispute letter for every item on that bureau. Then mail them all at once.
                    {(!user || user?.plan === "free") && (
                      <span className="text-[#D5FF3F] font-mono uppercase tracking-widest ml-1.5">
                        · Membership required
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {["equifax", "experian", "transunion"].map((b) => {
                  const count = byBureau[b] || 0;
                  if (count === 0) return null;
                  const busy = bulkLoading === b;
                  const isFree = !user || user?.plan === "free";
                  return (
                    <Button
                      key={b}
                      onClick={() => runBulk(b)}
                      disabled={!!bulkLoading}
                      data-testid={`bulk-draft-${b}`}
                      className={
                        isFree
                          ? "rounded-full h-10 px-4 bg-[#26282C] text-[#F5F3EE] hover:bg-[#333]"
                          : "rounded-full btn-outline h-10 px-4 hover:!border-[#D5FF3F] hover:!text-[#D5FF3F]"
                      }
                      title={isFree ? "Upgrade to draft letters" : undefined}
                    >
                      {busy ? (
                        <>
                          <Sparkles size={13} className="mr-1.5 animate-pulse text-[#D5FF3F]" />
                          Drafting {count} for {b.charAt(0).toUpperCase() + b.slice(1)}…
                        </>
                      ) : (
                        <>
                          Draft all {count} for {b.charAt(0).toUpperCase() + b.slice(1)}
                          {isFree ? (
                            <span className="ml-1.5 text-[10px] font-mono uppercase tracking-widest text-[#D5FF3F]">
                              · Upgrade
                            </span>
                          ) : (
                            <ArrowUpRight size={13} className="ml-1.5" />
                          )}
                        </>
                      )}
                    </Button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="card-surface overflow-hidden" data-testid="items-table">
          <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-[#26282C] bg-[#131518] text-[10px] uppercase tracking-widest text-[#6D716C] font-mono">
            <div className="col-span-4">Creditor</div>
            <div className="col-span-2">Type</div>
            <div className="col-span-2">Bureau</div>
            <div className="col-span-2">Amount</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {items.map((it) => (
            <div
              key={it.id}
              className="grid grid-cols-12 gap-4 px-6 py-4 items-center border-b border-[#26282C] last:border-0 hover:bg-[#131518] transition-colors"
              data-testid={`item-row-${it.id}`}
            >
              <div className="col-span-4">
                <div className="font-medium text-[#F5F3EE] flex items-center gap-2">
                  {it.creditor}
                  {it.item_group_id && (
                    <span
                      className="text-[9px] px-1.5 py-0.5 rounded-full font-mono uppercase tracking-widest bg-[#7CFF9B]/15 text-[#7CFF9B] flex items-center gap-1"
                      title="This account appears on multiple bureaus. Aura split it so you can dispute each independently."
                    >
                      <Layers size={9} /> tri-merge
                    </span>
                  )}
                </div>
                {it.account_number && (
                  <div className="text-xs text-[#6D716C] font-mono">#{it.account_number}</div>
                )}
              </div>
              <div className="col-span-2 text-sm capitalize text-[#A8ADA5]">
                {(it.item_type || "").replace("_", " ")}
              </div>
              <div className="col-span-2 text-sm capitalize text-[#A8ADA5]">
                {it.bureau}
                {it.source === "ai_scan" && it.bureau !== "all" && (
                  <span className="text-[9px] text-[#D5FF3F] font-mono uppercase tracking-widest ml-1.5">
                    · auto
                  </span>
                )}
              </div>
              <div className="col-span-2 text-sm font-mono text-[#F5F3EE]">
                {it.amount ? `$${Number(it.amount).toLocaleString()}` : "—"}
              </div>
              <div className="col-span-2 flex justify-end gap-1.5 flex-wrap">
                <ProofDocsButton item={it} />
                {user?.plan === "free" || !user ? (
                  <Button
                    size="sm"
                    onClick={() => openGenerate(it)}
                    data-testid={`generate-letter-${it.id}`}
                    className="rounded-full h-8 bg-[#26282C] text-[#F5F3EE] hover:bg-[#333]"
                    title="Membership required to draft letters"
                  >
                    <Sparkles size={13} className="mr-1" /> Letter · Upgrade
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => openGenerate(it)}
                    data-testid={`generate-letter-${it.id}`}
                    className="rounded-full btn-lime h-8"
                  >
                    <Sparkles size={13} className="mr-1" /> Letter
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => remove(it.id)}
                  data-testid={`delete-item-${it.id}`}
                  className="h-8 w-8 p-0 text-[#FF6B4A] hover:bg-[#FF6B4A]/10"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
          </div>
        </>
      )}

      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="max-w-lg bg-[#131518] border-[#26282C] text-[#F5F3EE]">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl">Draft letter</DialogTitle>
          </DialogHeader>
          {genItem && (
            <div className="space-y-4" data-testid="generate-dialog">
              <div className="text-sm text-[#A8ADA5]">
                For <span className="text-[#F5F3EE] font-medium">{genItem.creditor}</span>
              </div>

              {/* Evidence disclaimer — evidence dramatically improves dispute success. */}
              <div
                className="rounded-sm border border-[#7CFF9B]/25 bg-[#7CFF9B]/6 p-3 flex items-start gap-2.5"
                data-testid="draft-evidence-hint"
              >
                <span className="text-[#7CFF9B] font-mono text-[10px] uppercase tracking-widest flex-shrink-0 mt-0.5">
                  Tip
                </span>
                <div className="text-xs text-[#A8ADA5] leading-relaxed">
                  <span className="text-[#F5F3EE]">Attaching evidence makes letters go through way more often.</span>{" "}
                  Bare claims often bounce back &ldquo;verified as accurate.&rdquo; If you have a
                  receipt, court order, or&mdash;for identity theft&mdash;an{" "}
                  <a
                    href="https://www.identitytheft.gov/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#D5FF3F] underline hover:no-underline"
                    data-testid="draft-ftc-link"
                  >
                    FTC Identity Theft Report
                  </a>
                  , close this and tap <span className="text-[#F5F3EE]">&ldquo;Attach proof&rdquo;</span> first.
                </div>
              </div>

              {/* Recommendation callout — send to each bureau for max impact */}
              <div
                className="rounded-sm border border-[#D5FF3F]/25 bg-[#D5FF3F]/8 p-3 flex items-start gap-2.5"
                data-testid="all-bureaus-recommendation"
              >
                <Sparkles size={14} className="text-[#D5FF3F] flex-shrink-0 mt-0.5" />
                <div className="text-xs text-[#A8ADA5] leading-relaxed">
                  <span className="text-[#D5FF3F] font-medium">Pro move:</span> send a letter to
                  <span className="text-[#F5F3EE]"> each bureau reporting this item</span> — a
                  single-bureau dispute is easy to ignore, but three simultaneous demands force each
                  bureau to independently verify within 30 days (FCRA §1681i). We&rsquo;ll draft all
                  three at once — pick <span className="text-[#F5F3EE]">&ldquo;All 3 bureaus&rdquo;</span> below.
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest text-[#6D716C] font-mono">Bureau</Label>
                <Select value={genBureau} onValueChange={setGenBureau}>
                  <SelectTrigger data-testid="gen-bureau-select" className="mt-1 rounded-sm bg-[#0B0C0E] border-[#26282C]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#131518] border-[#26282C] text-[#F5F3EE]">
                    <SelectItem value="all" data-testid="gen-bureau-all">
                      All 3 bureaus (recommended)
                    </SelectItem>
                    <SelectItem value="equifax">Equifax only</SelectItem>
                    <SelectItem value="experian">Experian only</SelectItem>
                    <SelectItem value="transunion">TransUnion only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#6D716C] font-mono">Strategy</Label>
                <Select value={genStrategy} onValueChange={setGenStrategy}>
                  <SelectTrigger data-testid="gen-strategy-select" className="mt-1 rounded-sm bg-[#0B0C0E] border-[#26282C]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#131518] border-[#26282C] text-[#F5F3EE]">
                    {STRATEGIES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button disabled={genLoading} onClick={submitGenerate} data-testid="gen-submit-btn" className="rounded-full btn-lime">
                  {genLoading ? (
                    "Drafting…"
                  ) : genBureau === "all" ? (
                    <><Sparkles size={14} className="mr-2" />Draft 3 letters (all bureaus)</>
                  ) : (
                    <><Sparkles size={14} className="mr-2" />Draft with Claude</>
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
